import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import i18n, { LANGS, type Lang, useT } from '@/i18n';
import {
  authApi,
  type CompleteAuth,
  type TelegramContinuationPayload,
} from '@/lib/api/auth';
import { HttpError } from '@/lib/api/http';
import { googleCredential, telegramCredential } from './providers';
import { AuthVisual } from './AuthVisual';
import { BrandLockup } from '@/components/BrandLockup';
import { useBodyClass } from '@/app/providers/bodyClass';
import { syncChromeColor } from '@/lib/chrome';

type Notice = 'expired' | undefined;
type Step =
  | { kind: 'welcome' }
  | { kind: 'email'; mandatory: boolean; continuation?: TelegramContinuationPayload }
  | { kind: 'otp'; email: string; mandatory: boolean; continuation?: TelegramContinuationPayload; expiresAt: number; resendAt: number }
  | { kind: 'telegramMissing'; reason: 'notFound' | 'paidRequired' }
  | { kind: 'linkConfirmation'; token: string; expiresAt: number }
  | { kind: 'blocked'; reason: 'identity' | 'linking' | 'expired' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SECONDS = 30;

function safeErrorKey(error: unknown) {
  if (error instanceof HttpError) {
    if (error.kind === 'offline') return 'error.offline';
    const byCode: Record<string, string> = {
      invalid_or_expired_code: 'error.invalidCode',
      invalid_provider_credential: 'error.provider',
      invalid_or_expired_continuation: 'error.expiredFlow',
      invalid_or_expired_link: 'error.expiredLink',
      rate_limited: 'error.rateLimited',
      service_unavailable: 'error.service',
      database_unavailable: 'error.service',
      internal_error: 'error.service',
    };
    return byCode[error.code] ?? 'error.generic';
  }
  return 'error.provider';
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.7 4.7 0 0 1-2 3v2.6h3.3c1.9-1.8 2.9-4.4 2.9-7.5Z" />
      <path fill="currentColor" opacity=".82" d="M12 22c2.7 0 5-.9 6.7-2.3l-3.3-2.6c-.9.6-2.1 1-3.4 1a5.9 5.9 0 0 1-5.5-4.1H3.1v2.7A10 10 0 0 0 12 22Z" />
      <path fill="currentColor" opacity=".64" d="M6.5 14a6 6 0 0 1 0-3.9V7.4H3.1a10 10 0 0 0 0 9.3L6.5 14Z" />
      <path fill="currentColor" opacity=".46" d="M12 5.9c1.5 0 2.9.5 4 1.5l3-3A10 10 0 0 0 3.1 7.3L6.5 10A5.9 5.9 0 0 1 12 5.9Z" />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="m20.8 4.1-3 15c-.2 1.1-.8 1.4-1.7.9l-4.6-3.4-2.2 2.1c-.2.3-.5.5-.9.5l.3-4.7 8.5-7.7c.4-.3-.1-.5-.6-.2L6.1 13.2l-4.5-1.4c-1-.3-1-1 .2-1.5l17.6-6.8c.8-.3 1.6.2 1.4.6Z" />
    </svg>
  );
}

function ArrowIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>;
}

function LanguageSwitcher() {
  const { i18n: instance, t } = useT('auth');
  return (
    <div className="auth-langs" role="group" aria-label={t('language.label')}>
      {LANGS.map((language) => (
        <button
          key={language}
          type="button"
          className={instance.language === language ? 'active' : ''}
          aria-pressed={instance.language === language}
          onClick={() => void instance.changeLanguage(language)}
        >
          {t(`language.${language}`)}
        </button>
      ))}
    </div>
  );
}

/** Desktop: the large ORCA hero beside a clean sign-in column (the form is
 *  the main object, no card around it). Phone: the hero is left out — a
 *  compact stacked logo and the language switch, then the form. */
function AuthFrame({ children }: { children: ReactNode }) {
  useBodyClass('on-auth', true);
  useEffect(() => { syncChromeColor(); return () => syncChromeColor(); }, []);
  return (
    <main className="auth-page">
      <div className="auth-shell">
        <AuthVisual />
        <section className="auth-panel">
          <div className="auth-panel-top">
            <span className="auth-mark" aria-hidden="true"><img src="/assets/brand/orca-mark.png" alt="" width={34} height={34} /></span>
            <BrandLockup className="auth-lockup" />
            <LanguageSwitcher />
          </div>
          <div className="auth-panel-inner">{children}</div>
        </section>
      </div>
    </main>
  );
}

function FocusedView({ children, step = 'status' }: { children: ReactNode; step?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const next = ref.current?.querySelector<HTMLElement>('input[data-auth-focus], button[data-auth-focus], [data-auth-heading]');
    next?.focus();
  }, [step]);
  return <div ref={ref} className="auth-view auth-enter" data-auth-step={step}>{children}</div>;
}

function AuthButton({ children, onClick, busy, icon, tone = 'primary', type = 'button', disabled }: {
  children: ReactNode;
  onClick?: () => void;
  busy?: boolean;
  icon?: ReactNode;
  tone?: 'primary' | 'secondary' | 'telegram' | 'quiet';
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  const { t } = useT('auth');
  return (
    <button type={type} className={`auth-button ${tone}`} onClick={onClick} disabled={busy || disabled} aria-busy={busy || undefined}>
      <span className="auth-button-icon">{busy ? <i className="auth-spinner" aria-label={t('loading')} /> : icon}</span>
      <span>{children}</span>
      {!busy && tone !== 'quiet' ? <span className="auth-button-arrow"><ArrowIcon /></span> : null}
    </button>
  );
}

function Header({ eyebrow, title, text }: { eyebrow?: string; title: string; text?: string }) {
  return (
    <header className="auth-header">
      {eyebrow ? <span className="auth-eyebrow">{eyebrow}</span> : null}
      <h1 tabIndex={-1} data-auth-heading>{title}</h1>
      {text ? <p>{text}</p> : null}
    </header>
  );
}

function ErrorMessage({ text }: { text: string | null }) {
  return text ? <p id="auth-form-error" className="auth-error" role="alert">{text}</p> : null;
}

export function AuthStatusScreen({ title, text, loading, primary, onPrimary, secondary, onSecondary }: {
  title: string;
  text: string;
  loading?: boolean;
  primary?: string;
  onPrimary?: () => void;
  secondary?: string;
  onSecondary?: () => void;
}) {
  return (
    <AuthFrame>
      <FocusedView step="status">
        <div className="auth-status-mark" aria-hidden="true">{loading ? <i className="auth-spinner large" /> : <span>!</span>}</div>
        <Header title={title} text={text} />
        {(primary || secondary) && (
          <div className="auth-actions">
            {primary ? <AuthButton onClick={onPrimary}>{primary}</AuthButton> : null}
            {secondary ? <AuthButton tone="quiet" onClick={onSecondary}>{secondary}</AuthButton> : null}
          </div>
        )}
      </FocusedView>
    </AuthFrame>
  );
}

export function LinkConfirmationScreen({ busy, onConfirm, onCancel }: {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useT('auth');
  return (
    <AuthFrame>
      <FocusedView step="link-confirmation">
        <Header eyebrow={t('link.eyebrow')} title={t('link.title')} text={t('link.text')} />
        <div className="auth-preserve">
          <b>{t('link.keepTitle')}</b>
          <ul>
            <li>{t('link.keepPlan')}</li>
            <li>{t('link.keepSettings')}</li>
            <li>{t('link.keepSchedule')}</li>
          </ul>
          <p>{t('link.webFree')}</p>
        </div>
        <div className="auth-actions">
          <AuthButton busy={busy} onClick={onConfirm}>{t('link.confirm')}</AuthButton>
          <AuthButton tone="quiet" disabled={busy} onClick={onCancel}>{t('action.cancel')}</AuthButton>
        </div>
      </FocusedView>
    </AuthFrame>
  );
}

export function AuthScreen({ notice, onAuthenticated }: {
  notice?: Notice;
  onAuthenticated: CompleteAuth;
}) {
  const { t } = useT('auth');
  const [step, setStep] = useState<Step>({ kind: 'welcome' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'email' | 'otp' | 'google' | 'telegram' | 'link' | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (step.kind !== 'otp') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [step.kind]);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (step.kind === 'email' && !step.mandatory) setStep({ kind: 'welcome' });
      if (step.kind === 'otp' && !step.mandatory) setStep({ kind: 'email', mandatory: false });
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [step]);

  const showError = (next: unknown) => setError(t(safeErrorKey(next)));
  const resetError = () => setError(null);
  const restartExpiredFlow = (next: unknown) => {
    if (!(next instanceof HttpError) || next.code !== 'invalid_or_expired_continuation') return false;
    setStep({ kind: 'welcome' });
    setError(t('error.expiredFlow'));
    return true;
  };
  const toEmail = (options?: { mandatory?: boolean; continuation?: TelegramContinuationPayload }) => {
    resetError();
    setEmail('');
    setCode('');
    setStep({ kind: 'email', mandatory: !!options?.mandatory, continuation: options?.continuation });
  };

  const runGoogle = async () => {
    setBusy('google'); resetError();
    try {
      const flow = await authApi.googleStart();
      const credential = await googleCredential(flow);
      const session = await authApi.googleFinish(flow.flow_token, credential, i18n.language as Lang);
      await onAuthenticated(session);
    } catch (next) {
      if (next instanceof HttpError && next.code === 'identity_conflict') {
        setStep({ kind: 'blocked', reason: 'identity' });
      } else showError(next);
    } finally {
      setBusy(null);
    }
  };

  const runTelegram = async () => {
    setBusy('telegram'); resetError();
    try {
      const flow = await authApi.telegramStart();
      const credential = await telegramCredential(flow, i18n.language as Lang);
      const result = await authApi.telegramFinish(flow.flow_token, credential);
      if (result.authenticated) await onAuthenticated(result);
      else toEmail({ mandatory: true, continuation: result });
    } catch (next) {
      if (next instanceof HttpError && next.code === 'telegram_account_not_found') {
        setStep({ kind: 'telegramMissing', reason: 'notFound' });
      } else if (next instanceof HttpError && next.code === 'telegram_paid_access_required') {
        setStep({ kind: 'telegramMissing', reason: 'paidRequired' });
      } else showError(next);
    } finally {
      setBusy(null);
    }
  };

  const startEmail = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) return setError(t('error.email'));
    if (step.kind !== 'email') return;
    setBusy('email'); resetError();
    try {
      const result = step.continuation
        ? await authApi.telegramEmailStart(step.continuation.continuation_token, normalized, i18n.language as Lang)
        : await authApi.emailStart(normalized, i18n.language as Lang);
      const time = Date.now();
      setEmail(normalized);
      setCode('');
      setNow(time);
      setStep({
        kind: 'otp',
        email: normalized,
        mandatory: step.mandatory,
        continuation: step.continuation,
        expiresAt: time + result.expires_in * 1_000,
        resendAt: time + RESEND_COOLDOWN_SECONDS * 1_000,
      });
    } catch (next) {
      if (!restartExpiredFlow(next)) showError(next);
    } finally {
      setBusy(null);
    }
  };

  const finishEmail = async (event?: FormEvent) => {
    event?.preventDefault();
    if (step.kind !== 'otp') return;
    if (!/^\d{6}$/.test(code)) return setError(t('error.code'));
    if (Date.now() >= step.expiresAt) return setError(t('error.codeExpired'));
    setBusy('otp'); resetError();
    try {
      if (step.continuation) {
        const result = await authApi.telegramEmailFinish(step.continuation.continuation_token, step.email, code);
        if (result.authenticated) await onAuthenticated(result);
        else setStep({ kind: 'linkConfirmation', token: result.link_token, expiresAt: Date.now() + result.expires_in * 1_000 });
      } else {
        await onAuthenticated(await authApi.emailFinish(step.email, code));
      }
    } catch (next) {
      if (next instanceof HttpError && next.code === 'telegram_paid_access_required') {
        setStep({ kind: 'telegramMissing', reason: 'paidRequired' });
      } else if (next instanceof HttpError && next.code === 'identity_conflict') {
        setStep({ kind: 'blocked', reason: 'identity' });
      } else if (!restartExpiredFlow(next)) showError(next);
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    if (step.kind !== 'otp' || Date.now() < step.resendAt) return;
    setBusy('email'); resetError();
    try {
      const result = step.continuation
        ? await authApi.telegramEmailStart(step.continuation.continuation_token, step.email, i18n.language as Lang)
        : await authApi.emailStart(step.email, i18n.language as Lang);
      const time = Date.now();
      setCode(''); setNow(time);
      setStep({ ...step, expiresAt: time + result.expires_in * 1_000, resendAt: time + RESEND_COOLDOWN_SECONDS * 1_000 });
    } catch (next) {
      if (!restartExpiredFlow(next)) showError(next);
    } finally {
      setBusy(null);
    }
  };

  const confirmLink = async () => {
    if (step.kind !== 'linkConfirmation') return;
    if (Date.now() >= step.expiresAt) return setStep({ kind: 'blocked', reason: 'expired' });
    setBusy('link'); resetError();
    try {
      await onAuthenticated(await authApi.confirmTelegramEmailLink(step.token));
    } catch (next) {
      if (next instanceof HttpError && next.code === 'linking_unavailable') setStep({ kind: 'blocked', reason: 'linking' });
      else if (next instanceof HttpError && next.code === 'invalid_or_expired_link') setStep({ kind: 'blocked', reason: 'expired' });
      else showError(next);
    } finally {
      setBusy(null);
    }
  };

  const remaining = step.kind === 'otp' ? Math.max(0, Math.ceil((step.resendAt - now) / 1_000)) : 0;
  const actionBusy = busy !== null;

  return (
    <AuthFrame>
      {step.kind === 'welcome' && (
        <FocusedView step="welcome">
          <Header eyebrow={t('welcome.eyebrow')} title={t('welcome.title')} text={t('welcome.text')} />
          {notice === 'expired' ? <p className="auth-notice" role="status">{t('session.expired')}</p> : null}
          <div className="auth-actions primary-actions">
            <AuthButton icon={<GoogleIcon />} busy={busy === 'google'} disabled={actionBusy && busy !== 'google'} onClick={() => void runGoogle()}>{t('action.google')}</AuthButton>
            <AuthButton tone="secondary" disabled={actionBusy} onClick={() => toEmail()} icon={<span className="auth-mail-icon">@</span>}>{t('action.email')}</AuthButton>
          </div>
          <div className="auth-divider"><span>{t('welcome.or')}</span></div>
          <section className="auth-telegram-box" aria-labelledby="auth-tg-title">
            <div className="auth-tg-head">
              <span className="auth-tg-ic" aria-hidden="true"><TelegramIcon /></span>
              <div><b id="auth-tg-title">{t('telegram.offerTitle')}</b><p>{t('telegram.offerText')}</p></div>
            </div>
            <AuthButton tone="telegram" icon={<TelegramIcon />} busy={busy === 'telegram'} disabled={actionBusy && busy !== 'telegram'} onClick={() => void runTelegram()}>{t('action.telegram')}</AuthButton>
          </section>
          <ErrorMessage text={error} />
          <p className="auth-legal">{t('welcome.legal')}</p>
        </FocusedView>
      )}

      {step.kind === 'email' && (
        <FocusedView step="email">
          <Header
            eyebrow={step.mandatory ? t('email.requiredEyebrow') : t('email.eyebrow')}
            title={step.mandatory ? t('email.requiredTitle') : t('email.title')}
            text={step.mandatory ? t('email.requiredText') : t('email.text')}
          />
          <form className="auth-form" onSubmit={startEmail} noValidate>
            <label htmlFor="auth-email">{t('email.label')}</label>
            <input
              id="auth-email"
              data-auth-focus
              type="email"
              value={email}
              disabled={actionBusy}
              onChange={(event) => { setEmail(event.target.value); resetError(); }}
              placeholder={t('email.placeholder')}
              autoComplete="email"
              inputMode="email"
              aria-invalid={!!error || undefined}
              aria-describedby={error ? 'auth-form-error' : undefined}
            />
            <ErrorMessage text={error} />
            <AuthButton type="submit" busy={busy === 'email'}>{t('action.continue')}</AuthButton>
          </form>
          {!step.mandatory ? <button type="button" className="auth-back" disabled={actionBusy} onClick={() => setStep({ kind: 'welcome' })}>{t('action.back')}</button> : null}
        </FocusedView>
      )}

      {step.kind === 'otp' && (
        <FocusedView step="otp">
          <Header eyebrow={t('otp.eyebrow')} title={t('otp.title')} text={t('otp.text', { email: step.email })} />
          <form className="auth-form" onSubmit={finishEmail} noValidate>
            <label htmlFor="auth-code">{t('otp.label')}</label>
            <input
              id="auth-code"
              className="auth-code"
              data-auth-focus
              type="text"
              value={code}
              disabled={actionBusy}
              onChange={(event) => { setCode(event.target.value.replace(/\D/g, '').slice(0, 6)); resetError(); }}
              onPaste={(event) => {
                const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                if (pasted) { event.preventDefault(); setCode(pasted); resetError(); }
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={6}
              aria-invalid={!!error || undefined}
              aria-describedby={`auth-code-help${error ? ' auth-form-error' : ''}`}
            />
            <p id="auth-code-help" className="auth-help" role="status">{Date.now() >= step.expiresAt ? t('otp.expired') : t('otp.help')}</p>
            <ErrorMessage text={error} />
            <AuthButton type="submit" busy={busy === 'otp' || busy === 'email'}>{t('action.verify')}</AuthButton>
          </form>
          <div className="auth-secondary-row">
            <button type="button" disabled={remaining > 0 || actionBusy} onClick={() => void resend()}>
              {remaining > 0 ? t('otp.resendIn', { seconds: remaining }) : t('otp.resend')}
            </button>
            {!step.mandatory ? <button type="button" disabled={actionBusy} onClick={() => setStep({ kind: 'email', mandatory: false })}>{t('otp.changeEmail')}</button> : null}
          </div>
        </FocusedView>
      )}

      {step.kind === 'telegramMissing' && (
        <FocusedView step="telegram-result">
          <div className="auth-status-mark"><TelegramIcon /></div>
          <Header
            title={t(step.reason === 'notFound' ? 'telegram.notFoundTitle' : 'telegram.paidTitle')}
            text={t(step.reason === 'notFound' ? 'telegram.notFoundText' : 'telegram.paidText')}
          />
          <div className="auth-actions">
            <AuthButton disabled={actionBusy} onClick={() => toEmail()}>{t('action.email')}</AuthButton>
            <AuthButton tone="secondary" busy={busy === 'google'} disabled={actionBusy && busy !== 'google'} onClick={() => void runGoogle()}>{t('action.google')}</AuthButton>
            {step.reason === 'notFound' ? <a className="auth-back" href="https://t.me/Orcafamily_bot" target="_blank" rel="noreferrer">{t('telegram.openBot')}</a> : null}
          </div>
          <ErrorMessage text={error} />
        </FocusedView>
      )}

      {step.kind === 'linkConfirmation' && (
        <FocusedView step="link-confirmation">
          <Header eyebrow={t('link.eyebrow')} title={t('link.title')} text={t('link.text')} />
          <div className="auth-preserve">
            <b>{t('link.keepTitle')}</b>
            <ul><li>{t('link.keepPlan')}</li><li>{t('link.keepSettings')}</li><li>{t('link.keepSchedule')}</li></ul>
            <p>{t('link.webFree')}</p>
          </div>
          <ErrorMessage text={error} />
          <div className="auth-actions">
            <AuthButton busy={busy === 'link'} onClick={() => void confirmLink()}>{t('link.confirm')}</AuthButton>
            <AuthButton tone="quiet" disabled={actionBusy} onClick={() => { resetError(); setStep({ kind: 'welcome' }); }}>{t('action.cancel')}</AuthButton>
          </div>
        </FocusedView>
      )}

      {step.kind === 'blocked' && (
        <FocusedView step="blocked">
          <div className="auth-status-mark" aria-hidden="true"><span>!</span></div>
          <Header
            title={t(step.reason === 'identity' ? 'conflict.title' : step.reason === 'expired' ? 'link.expiredTitle' : 'blocked.title')}
            text={t(step.reason === 'identity' ? 'conflict.text' : step.reason === 'expired' ? 'link.expiredText' : 'blocked.text')}
          />
          <div className="auth-actions">
            <AuthButton onClick={() => { resetError(); setStep({ kind: 'welcome' }); }}>{t('action.backToSignIn')}</AuthButton>
          </div>
        </FocusedView>
      )}
    </AuthFrame>
  );
}
